import { isPlatformBrowser } from '@angular/common';
import {
  Injectable,
  PLATFORM_ID,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { usePreset } from '@openng/optimus-ui-themes';
import {
  DARK_MODE_CLASS,
  DEFAULT_THEME_PRESET_ID,
  THEME_PRESETS,
  ThemePresetId,
  ThemePresetOption,
} from './presets';

const LS_PRESET_KEY = 'user.theme.preset';
const LS_DARK_KEY = 'user.theme.dark';

/**
 * Owns the active theme preset and colour scheme.
 *
 * `provideOptimus` applies the default preset at bootstrap, so the first effect
 * run skips the redundant re-apply when the stored choice is already the one
 * that was installed.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  private readonly _presetId = signal<ThemePresetId>(this.readPresetId());
  private readonly darkRequested = signal<boolean>(this.readDark());
  private hasAppliedPreset = false;

  readonly presets = THEME_PRESETS;
  readonly presetId = this._presetId.asReadonly();

  readonly activePreset = computed<ThemePresetOption>(
    () =>
      THEME_PRESETS.find((p) => p.id === this._presetId()) ?? THEME_PRESETS[0],
  );

  /** Dark mode only takes effect on presets that define a dark scheme. */
  readonly darkMode = computed(
    () => this.darkRequested() && this.activePreset().supportsDark,
  );

  constructor() {
    if (!this.isBrowser) return;

    effect(() => {
      const preset = this.activePreset();
      if (this.hasAppliedPreset || preset.id !== DEFAULT_THEME_PRESET_ID) {
        usePreset(preset.preset);
      }
      this.hasAppliedPreset = true;
      localStorage.setItem(LS_PRESET_KEY, preset.id);
    });

    effect(() => {
      document.documentElement.classList.toggle(
        DARK_MODE_CLASS,
        this.darkMode(),
      );
      localStorage.setItem(LS_DARK_KEY, String(this.darkRequested()));
    });
  }

  setPreset(id: ThemePresetId): void {
    this._presetId.set(id);
  }

  setDarkMode(dark: boolean): void {
    this.darkRequested.set(dark);
  }

  toggleDarkMode(): void {
    this.darkRequested.update((dark) => !dark);
  }

  private readPresetId(): ThemePresetId {
    if (!isPlatformBrowser(this.platformId)) return DEFAULT_THEME_PRESET_ID;
    const stored = localStorage.getItem(LS_PRESET_KEY);
    return THEME_PRESETS.some((p) => p.id === stored)
      ? (stored as ThemePresetId)
      : DEFAULT_THEME_PRESET_ID;
  }

  private readDark(): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;
    const stored = localStorage.getItem(LS_DARK_KEY);
    if (stored !== null) return stored === 'true';
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
  }
}
